import { lazy } from 'react'
import BetaServicesAddendumPage from '@/pages/beta'
import ForgotPasswordPage from '@/pages/forgot-password'
// Import auth pages
import LoginPage from '@/pages/login'
import LoginConfirmationPage from '@/pages/login/confirmation'
import NotFoundPage from '@/pages/not-found'
import RegisterPage from '@/pages/register'
import RegisterConfirmationPage from '@/pages/register/confirmation'
import ResendCodePage from '@/pages/register/resend-code'
import ResetPasswordPage from '@/pages/reset-password'
import UnauthorizedPage from '@/pages/unauthorized'

import { AuthRouteLayout, LazyLoad } from '../components'
import { AppRouteObject } from '../types'

// Lazy loaded pages
const InvitePage = lazy(() => import('@/pages/invite'))
const ExternalOrgInvitePage = lazy(() => import('@/pages/external-org-invite'))
const ExternalOrgSupportRequestPage = lazy(
  () => import('@/pages/external-org-support-request'),
)
const ClientPortalInvitePage = lazy(
  () => import('@/pages/client-portal/invite'),
)
const GoogleCallbackPage = lazy(() => import('@/pages/auth/google-callback'))
const GoogleCalendarCallbackPage = lazy(
  () => import('@/pages/auth/google-calendar-callback'),
)
const GmailCallbackPage = lazy(() => import('@/pages/auth/gmail-callback'))
const OutlookCallbackPage = lazy(() => import('@/pages/auth/outlook-callback'))
const OutlookCalendarCallbackPage = lazy(
  () => import('@/pages/auth/outlook-calendar-callback'),
)
const ZoomCallbackPage = lazy(() => import('@/pages/auth/zoom-callback'))
const PublicFormPage = lazy(() => import('@/pages/f/[slug]'))
const PublicEventPage = lazy(() => import('@/pages/e/[slug]'))
const PublicRoadmapPage = lazy(() => import('@/pages/roadmap/public-roadmap'))
const PublicSharedListPage = lazy(() => import('@/pages/share/list'))
const PublicBookingPage = lazy(() => import('@/pages/public-booking'))
const PublicBookingCancelPage = lazy(
  () => import('@/pages/public-booking/cancel'),
)
const PublicQuotePage = lazy(() => import('@/pages/public-quote'))
const PublicKbPage = lazy(() => import('@/pages/public-kb'))
const PublicKbArticlePage = lazy(() =>
  import('@/pages/public-kb/article').then(m => ({
    default: m.PublicKbArticlePage,
  })),
)
const EmbeddedFormPage = lazy(() => import('@/pages/embed/forms/[slug]'))
const OpenPage = lazy(() => import('@/pages/open'))
const McpAuthorizePage = lazy(() => import('@/pages/mcp/authorize'))

// Authentication routes configuration
export const authRoutes: AppRouteObject[] = [
  {
    element: <AuthRouteLayout />,
    children: [
      // Authentication routes (not protected)
      {
        path: '/login',
        element: <LoginPage />,
        title: 'Login',
      },
      {
        path: '/login/confirmation',
        element: <LoginConfirmationPage />,
        title: 'Login Confirmation',
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
        title: 'Forgot Password',
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
        title: 'Reset Password',
      },
      {
        path: '/register',
        element: <RegisterPage />,
        title: 'Register',
      },
      {
        path: '/register/confirmation',
        element: <RegisterConfirmationPage />,
        title: 'Register Confirmation',
      },
      {
        path: '/register/resend-code',
        element: <ResendCodePage />,
        title: 'Resend Code',
      },
      {
        path: '/unauthorized',
        element: <UnauthorizedPage />,
        title: 'Unauthorized',
      },
      {
        path: '/invite',
        element: (
          <LazyLoad>
            <InvitePage />
          </LazyLoad>
        ),
        title: 'Invite',
      },
      {
        path: '/external-org-invite',
        element: (
          <LazyLoad>
            <ExternalOrgInvitePage />
          </LazyLoad>
        ),
        title: 'External Organization Invite',
      },
      {
        path: '/external-org-support-request',
        element: (
          <LazyLoad>
            <ExternalOrgSupportRequestPage />
          </LazyLoad>
        ),
        title: 'External Organization Support Request',
      },
      {
        path: '/client-portal/invite',
        element: (
          <LazyLoad>
            <ClientPortalInvitePage />
          </LazyLoad>
        ),
        title: 'Client Portal Invite',
      },
      {
        path: '/auth/google/callback',
        element: (
          <LazyLoad>
            <GoogleCallbackPage />
          </LazyLoad>
        ),
        title: 'Google Sign In',
      },
      {
        path: '/auth/callback',
        element: (
          <LazyLoad>
            <GoogleCallbackPage />
          </LazyLoad>
        ),
        title: 'OAuth Callback',
      },
      // Google Calendar OAuth callback
      {
        path: '/auth/google-calendar-callback',
        element: (
          <LazyLoad>
            <GoogleCalendarCallbackPage />
          </LazyLoad>
        ),
        title: 'Google Calendar Connection',
      },
      // Gmail OAuth callback
      {
        path: '/auth/gmail-callback',
        element: (
          <LazyLoad>
            <GmailCallbackPage />
          </LazyLoad>
        ),
        title: 'Gmail Connection',
      },
      // Outlook email OAuth callback
      {
        path: '/auth/outlook-callback',
        element: (
          <LazyLoad>
            <OutlookCallbackPage />
          </LazyLoad>
        ),
        title: 'Outlook Connection',
      },
      // Outlook Calendar OAuth callback
      {
        path: '/auth/outlook-calendar-callback',
        element: (
          <LazyLoad>
            <OutlookCalendarCallbackPage />
          </LazyLoad>
        ),
        title: 'Outlook Calendar Connection',
      },
      {
        path: '/auth/zoom-callback',
        element: (
          <LazyLoad>
            <ZoomCallbackPage />
          </LazyLoad>
        ),
        title: 'Zoom Connection',
      },
      {
        path: '/beta',
        element: <BetaServicesAddendumPage />,
        title: 'Beta Services Addendum',
      },
      // MCP OAuth consent screen (Scrambl-hosted authorization)
      {
        path: '/mcp/authorize/:request',
        element: (
          <LazyLoad>
            <McpAuthorizePage />
          </LazyLoad>
        ),
        title: 'Authorize Connection',
      },
      // Deep link redirect page - handles opening shared links in desktop app
      {
        path: '/open',
        element: (
          <LazyLoad>
            <OpenPage />
          </LazyLoad>
        ),
        title: 'Open in Scrambl',
      },
      // 404 route
      {
        path: '*',
        element: <NotFoundPage />,
        title: '404 - Not Found',
      },
    ],
  },
]

// Public form routes - exported separately to ensure they're at the top of the route list
// These must be matched BEFORE /:orgSlug to prevent "f" from being treated as an org slug
// Public booking pages (no auth) — cancel route first so 'cancel' never
// resolves as an org slug.
export const publicBookingRoutes: AppRouteObject[] = [
  {
    path: '/book/cancel/:token',
    element: (
      <LazyLoad>
        <PublicBookingCancelPage />
      </LazyLoad>
    ),
    title: 'Cancel booking',
  },
  {
    path: '/book/:orgSlug/:linkSlug',
    element: (
      <LazyLoad>
        <PublicBookingPage />
      </LazyLoad>
    ),
    title: 'Book a meeting',
  },
]

// Public quote pages (no auth).
export const publicQuoteRoutes: AppRouteObject[] = [
  {
    path: '/q/:token',
    element: (
      <LazyLoad>
        <PublicQuotePage />
      </LazyLoad>
    ),
    title: 'Quote',
  },
]

// Public knowledge base pages (no auth) — article route listed first.
export const publicKbRoutes: AppRouteObject[] = [
  {
    path: '/kb/:orgSlug/a/:articleSlug',
    element: (
      <LazyLoad>
        <PublicKbArticlePage />
      </LazyLoad>
    ),
    title: 'Help article',
  },
  {
    path: '/kb/:orgSlug',
    element: (
      <LazyLoad>
        <PublicKbPage />
      </LazyLoad>
    ),
    title: 'Help center',
  },
]

export const publicFormRoutes: AppRouteObject[] = [
  {
    path: '/f/:slug',
    element: (
      <LazyLoad>
        <PublicFormPage />
      </LazyLoad>
    ),
    title: 'Form',
  },
  {
    path: '/embed/forms/:slug',
    element: (
      <LazyLoad>
        <EmbeddedFormPage />
      </LazyLoad>
    ),
    title: 'Embedded Form',
  },
]

export const publicEventRoutes: AppRouteObject[] = [
  {
    path: '/e/:slug',
    element: (
      <LazyLoad>
        <PublicEventPage />
      </LazyLoad>
    ),
    title: 'Event',
  },
]

// Public shared lists — unauthenticated guest view of a personal list,
// registered before /:orgSlug so "share" isn't treated as an org slug.
export const publicListShareRoutes: AppRouteObject[] = [
  {
    path: '/share/list/:token',
    element: (
      <LazyLoad>
        <PublicSharedListPage />
      </LazyLoad>
    ),
    title: 'Shared list',
  },
]

// Public roadmap — unauthenticated, registered before /:orgSlug so "roadmap"
// isn't treated as an org slug.
export const publicRoadmapRoutes: AppRouteObject[] = [
  {
    path: '/roadmap/:orgSlug',
    element: (
      <LazyLoad>
        <PublicRoadmapPage />
      </LazyLoad>
    ),
    title: 'Roadmap',
  },
]
